import {
  BadgeCheck,
  Clock3,
  Shield,
  CircleHelp,
  LogOut,
  ChevronRight,
  X,
  AlertCircle,
  Clock,
  Phone,
  Pencil,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMe } from "../hooks/useMe";
import { useAuthMutations } from "../hooks/useAuthMutations";
import { usePinStatus } from "../hooks/usePin";

export function ProfileOverlay({
  onClose,
  onActivityPress,
}) {
  const navigate = useNavigate();
  const { profile, isLoadingProfile, kycProfiles, isLoadingKyc } = useMe();
  const { logout, isLoggingOut } = useAuthMutations();
  const { data: pinStatus } = usePinStatus();
  const pinConfigured = Boolean(pinStatus?.configured);

  if (isLoadingProfile) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="mx-4 mb-4 rounded-[32px] bg-[#F8F8F6] shadow-xl overflow-hidden">
          <div className="bg-[#0A4730] px-6 pt-5 pb-6 h-28 animate-pulse" />
          <div className="p-4 space-y-3">
            <div className="h-16 rounded-2xl bg-gray-200 animate-pulse" />
            <div className="h-36 rounded-2xl bg-gray-200 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  const initial = profile?.full_name?.split(" ")[0]?.charAt(0)?.toUpperCase() ?? "?";

  const kyc = kycProfiles?.[0] ?? null;
  const kycState = kyc?.status ?? "not_started";

  const kycConfig = {
      not_started: {
        bg: "bg-amber-50",
        icon: <AlertCircle size={20} className="text-amber-500" />,
        title: "Verify your identity",
        sub: "Complete KYC to unlock transfers",
      },
      pending: {
        bg: "bg-blue-50",
        icon: <Clock size={20} className="text-blue-500" />,
        title: "Verification in review",
        sub: "We'll notify you once it's approved",
      },
      approved: {
        bg: "bg-[#E8F5E1]",
        icon: <BadgeCheck size={20} className="text-[#0A4730]" />,
        title: "Verified Account",
        sub: "Your identity has been verified",
      },
      rejected: {
        bg: "bg-red-50",
        icon: <AlertCircle size={20} className="text-red-500" />,
        title: "Verification failed",
        sub: "Please resubmit your documents",
      },
      expired: {
        bg: "bg-amber-50",
        icon: <AlertCircle size={20} className="text-amber-500" />,
        title: "Verification expired",
        sub: "Please verify your identity again",
      },
    };

  const { bg, icon, title, sub } = kycConfig[kycState] ?? kycConfig.not_started;

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="mx-4 mb-4 rounded-[32px] bg-[#F8F8F6] shadow-xl overflow-hidden">

        {/* Header */}
        <div className="bg-[#0A4730] px-6 pt-5 pb-6">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#FFB800] flex items-center justify-center font-bold text-[#0A4730] text-lg">
                {initial}
              </div>

              <div>
                <h2 className="text-white font-semibold text-lg leading-tight">
                  {profile?.full_name ?? "—"}
                </h2>
                <p className="text-white/70 text-sm">
                  {profile?.email ?? "—"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  onClose();
                  navigate("/profile/edit");
                }}
                className="p-1.5 rounded-full hover:bg-white/10 transition"
              >
                <Pencil size={15} className="text-white/70" />
              </button>

              <button
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-white/10 transition"
              >
                <X size={15} className="text-white/70" />
              </button>
            </div>
          </div>

          {/* Pills */}
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            {profile?.phone_number && (
              <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1">
                <Phone size={12} className="text-white/60" />
                <span className="text-white/80 text-xs">
                  {profile.phone_number}
                </span>
              </div>
            )}
            {profile?.default_currency && (
              <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1">
                <span className="text-white/80 text-xs">
                  {profile.default_currency} · {profile.country_code}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">

          {/* KYC */}
          <button
            onClick={() => {
              onClose();
              navigate("/kyc");
            }}
            className={`w-full ${bg} rounded-2xl p-4 flex items-center gap-3 text-left transition active:scale-[0.98] cursor-pointer`}
          >
            {isLoadingKyc
              ? <div className="w-5 h-5 rounded-full bg-gray-300 animate-pulse" />
              : icon
            }
            <div>
              <p className="font-medium text-[#0A4730] text-sm">{title}</p>
              <p className="text-xs text-[#0A4730]/60">{sub}</p>
            </div>
          </button>

          {/* Menu */}
          <div className="mt-4 bg-white rounded-2xl overflow-hidden border border-[#0a2540]/5">
            <MenuItem
              icon={<Clock3 size={18} />}
              title="Activity History"
              onClick={onActivityPress}
            />
            <MenuItem
              icon={<Shield size={18} />}
              title={pinConfigured ? "Change PIN" : "Set Up PIN"}
              onClick={() => {
                onClose();
                navigate(pinConfigured ? "/pin/change" : "/pin/setup", {
                  state: { returnTo: "/home" },
                });
              }}
            />
            <MenuItem
            icon={<CircleHelp size={18} />}
            title="Help & Support"
            onClick={() => {
              onClose();
              navigate("/support");
            }}
          />
          </div>

          {/* Logout */}
          <button
            onClick={() =>
              logout(undefined, {
                onSuccess: () => {
                  onClose();
                  navigate("/login");
                },
              })
            }
            disabled={isLoggingOut}
            className="mt-4 w-full rounded-2xl bg-white border border-red-100 py-3 flex items-center justify-center gap-2 transition active:scale-[0.98] disabled:opacity-50"
          >
            <LogOut size={16} className="text-red-400" />
            <span className="text-red-500 font-medium text-sm">
              {isLoggingOut ? "Signing out..." : "Sign Out"}
            </span>
          </button>

        </div>
      </div>
    </div>
  );
}

function MenuItem({ icon, title, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-4 flex items-center justify-between hover:bg-slate-50 transition border-b border-[#0a2540]/5 last:border-0"
    >
      <div className="flex items-center gap-3 text-[#0a2540]">
        {icon}
        <span className="text-sm">{title}</span>
      </div>
      <ChevronRight size={16} className="text-[#0a2540]/30" />
    </button>
  );
}
